import type {TSubmissionStatus} from "@/hooks/useContentApproval";
import {create} from "@/store/createStore";
import type {TInstagramContent, TInstagramMedia, TNonFunctionKeys} from "@/store/types";
import {produce} from "immer";
import {isIE} from "react-device-detect";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export type TCarouselMedia = TInstagramContent &
  TCarouselSubmission & {
    assetName?: string | null;
    campaign?: TCarouselCampaign; // Used for carousels with media from different campaigns (e.g. favorite content)
    date?: string;
  };

type TCarouselCampaign = {id: number; title: string};

type TCarouselSubmission = {
  isRevisionAvailable?: boolean;
  submissionId?: number;
  submissionStatus?: TSubmissionStatus;
};

export type TCarouselVariant = "assets" | "content_approval" | "default" | "gallery" | "rating" | "social_feed";

type TState = {
  campaign?: TCarouselCampaign; // Used for carousels with media from the same campaign (e.g. gallery)
  contentSectionWidth: number;
  currentIndex: number;
  currentMedia: TCarouselMedia[];
  defaultAlbumItem?: TInstagramMedia | null;
  hideDownloadOption: boolean;
  initialMediaIndex: number;
  initialTimestamp: number | null;
  isIENotSupportedModalOpen: boolean;
  isOpen: boolean;
  mediaLength?: number;
  shouldLoop: boolean;
  shouldUsePersistedIndex: boolean;
  showCaption: boolean;
  showFooter: boolean;
  staticFooter: boolean;
  variant: TCarouselVariant;
  onChange?: (index: number) => void;
  open({
    campaign,
    hideDownloadOption,
    initialMediaId,
    media,
    mediaLength,
    onChange,
    shouldLoop,
    shouldUsePersistedIndex,
    showCaption,
    showFooter,
    staticFooter,
    variant,
  }: {
    campaign?: TCarouselCampaign;
    hideDownloadOption?: boolean;
    initialMediaId?: number;
    media: TCarouselMedia[];
    mediaLength?: number;
    onChange?: (index: number) => void;
    shouldLoop?: boolean;
    shouldUsePersistedIndex?: boolean;
    showCaption?: boolean;
    showFooter?: boolean;
    staticFooter?: boolean;
    variant?: TCarouselVariant;
  }): void;
  reset(): void;
  setCurrentIndex(index: number): void;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
  toggleLikedStatus(contentId: number): void;
};

const createCarousel = (storePrefix: string = "") =>
  create<TState>()(
    immer(
      devtools(
        set => ({
          contentSectionWidth: 0,
          currentIndex: 0,
          currentMedia: [],
          defaultAlbumItem: null,
          hideDownloadOption: false,
          initialMediaIndex: 0,
          initialTimestamp: null,
          isIENotSupportedModalOpen: false,
          isOpen: false,
          shouldLoop: true,
          shouldUsePersistedIndex: false,
          showCaption: true,
          showFooter: true,
          staticFooter: false,
          variant: "default",
          open: ({
            campaign,
            hideDownloadOption = false,
            initialMediaId,
            media,
            mediaLength,
            onChange,
            shouldLoop = true,
            shouldUsePersistedIndex = false,
            showCaption = true,
            showFooter = true,
            staticFooter = false,
            variant = "default",
          }) => {
            if (isIE) {
              return set(
                state => {
                  state.isIENotSupportedModalOpen = true;
                },
                false,
                `useCarousel${storePrefix}Store/open`,
              );
            }

            const initialMediaIndex = initialMediaId ? media.findIndex(media => media.id === initialMediaId) : 0;

            return set(
              state => {
                state.campaign = campaign;
                state.hideDownloadOption = hideDownloadOption;
                state.showCaption = showCaption;
                state.showFooter = showFooter;
                state.staticFooter = staticFooter;
                state.currentMedia = media;
                state.currentIndex = initialMediaIndex;
                state.initialMediaIndex = initialMediaIndex;
                state.isOpen = true;
                state.mediaLength = mediaLength;
                state.onChange = onChange;
                state.shouldLoop = shouldLoop;
                state.shouldUsePersistedIndex = shouldUsePersistedIndex;
                state.variant = variant;
              },
              false,
              `useCarousel${storePrefix}Store/open`,
            );
          },
          reset: () =>
            set(
              state => {
                state.defaultAlbumItem = null;
                state.hideDownloadOption = false;
                state.initialTimestamp = null;
                state.isOpen = false;

                if (!storePrefix) {
                  state.currentMedia = [];
                  state.initialMediaIndex = 0;
                }

                state.showCaption = true;
                state.showFooter = true;
              },
              false,
              `useCarousel${storePrefix}Store/reset`,
            ),
          setCurrentIndex: index =>
            set(
              state => {
                state.currentIndex = index;
              },
              false,
              `useCarousel${storePrefix}Store/setCurrentIndex`,
            ),
          setValue: (key, value) =>
            set(
              state => {
                state[key] = value;
              },
              false,
              `useCarousel${storePrefix}Store/setValue`,
            ),
          toggleLikedStatus: (contentId: number) =>
            set(
              produce((state: TState) => {
                const mediaItem = state.currentMedia.find(media => media.instagram_media?.id === contentId);

                if (mediaItem?.instagram_media) {
                  mediaItem.instagram_media.liked = !mediaItem.instagram_media.liked;
                }
              }),
            ),
        }),
        {
          name: `useCarousel${storePrefix}Store`,
        },
      ),
    ),
  );

const useCarouselStore = createCarousel();
const useAdminCarouselStore = createCarousel("Admin");

export {useAdminCarouselStore, useCarouselStore};
